import {
  login,
  logout,
  refreshToken as refreshTokenUsingApi,
  // revokeToken,
} from "@/api/user";
import { notifyMobileAppLogout, notifyMobileAppTokenRefresh } from "@/utils/mobile-auth";
import {
  getToken,
  setToken,
  removeToken,
  getRefreshToken,
  setRefreshToken,
  removeRefreshToken,
  getTokenExpiration,
  setTokenExpiration,
  removeTokenExpiration,
  getUserData,
  setUserData,
  clearAuthData,
  isTokenExpired,
  isMobileApp as getMobileAppFromCookie,
  setMobileApp,
} from "@/utils/auth";
import { USER_ROLES } from "@/utils/user-roles";
import router, { resetRouter } from "@/router";
import jwt_decode from "jwt-decode";
import { useStore } from "vuex";
import store from "@/store";
import { ALLOWED_UNAUTH_ROUTE_NAMES } from "@/router";
import { useRoute, useRouter } from "vue-router";

const state = {
  accessToken: getToken(),
  refreshToken: getRefreshToken(),
  tokenExpiration: getTokenExpiration(),
  userData: getUserData(),
  roles: [],
  isAdmin: false,
  isInitialEvaluator: false,
  isAdminEntity: false,
  isEntityReviewer: false,
  isMobileApp: getMobileAppFromCookie(), // Read from cookie on initialization
};

const getters = {
  userRoles: (state) => state.userData?.roles || [],
  isAdmin: (state) =>
    state.userData?.roles?.includes(USER_ROLES.ADMIN) || false,
  isInitialEvaluator: (state) =>
    state.userData?.roles?.includes(USER_ROLES.INITIAL_EVALUATOR) || false,
  isAdminEntity: (state) =>
    state.userData?.roles?.includes(USER_ROLES.ADMIN_ENTITY) || false,
  isEntityReviewer: (state) =>
    state.userData?.roles?.includes(USER_ROLES.ENTITY_REVIEWER) || false,
  userEmail: (state) => state.userData?.email,
  isAuthenticated: (state) => {
    const token = state.accessToken;
    const expiration = state.tokenExpiration;

    if (!token || !expiration) {
      return false;
    }

    return !isTokenExpired();
  },
  isMobileApp: (state) => state.isMobileApp,
  isRouteAllowedWithoutAuth: () => (routeName) => {
    if (!routeName) return false;
    // Use route name for robust matching
    const isAllowed = ALLOWED_UNAUTH_ROUTE_NAMES.includes(routeName);
    return isAllowed;
  },
  getAllowedUnauthRoutes: () => ALLOWED_UNAUTH_ROUTE_NAMES,
};

const mutations = {
  SET_AUTH_DATA(
    state,
    { accessToken, refreshToken, accessTokenExpiration, userData, isMobileApp }
  ) {
    state.accessToken = accessToken;
    state.refreshToken = refreshToken;
    state.tokenExpiration = accessTokenExpiration;
    state.userData = userData;
    state.roles = userData.roles;

    // Set mobile app flag if provided
    if (isMobileApp !== undefined) {
      state.isMobileApp = isMobileApp;
      setMobileApp(isMobileApp); // Persist to cookie
    }

    if (accessToken) setToken(accessToken, accessTokenExpiration);
    if (refreshToken) setRefreshToken(refreshToken);
    if (accessTokenExpiration) setTokenExpiration(accessTokenExpiration);
    if (userData) setUserData(userData);
  },

  SET_MOBILE_APP(state, isMobileApp) {
    state.isMobileApp = isMobileApp;
    setMobileApp(isMobileApp); // Persist to cookie
  },

  CLEAR_AUTH_DATA(state) {
    state.accessToken = null;
    state.refreshToken = null;
    state.tokenExpiration = null;
    state.userData = null;
    state.roles = [];
    state.isMobileApp = false;
    clearAuthData();
  },
};
export const getAuthedRoute=(role, isMobileApp = false)=>{
  // Define routes for each role
  const isAdmin  = role === 'Admin' || role === 'AdminEntity'
  if(isAdmin) {
    // Mobile users go to mobile-friendly dashboard, desktop to full statistics
    return isMobileApp ? '/inspection-requests' : '/statistics'
  }

  const isInitialEvaluator = role === 'InitialInspectionApprover' || role === 'InitialEvaluator' || role === 'MinistryReviewer'
  if(isInitialEvaluator) {
    // Mobile users get simplified view, desktop gets full view
    return isMobileApp ? '/inspection-requests' : '/inspection-requests'
  }

  const isDetailedEvaluator = role === 'DetailedInspectionApprover' || role === 'DetailedEvaluator'
  if(isDetailedEvaluator) {
    return isMobileApp ? '/inspection-requests-v2' : '/inspection-requests-v2'
  }

  const isEntityReviewer = role === 'EntityReviewer'
  if(isEntityReviewer) {
    return isMobileApp ? '/inspection-requests' : '/inspection-requests'
  }

  // Default fallback
  return '/login'
}
const actions = {
  async login({ commit }, { email, password, redirectTo }) {
    try {
      const response = await login({ email, password });
      console.log("response", response);
      const { accessToken, refreshToken, accessTokenExpiration, email: userEmail, roles } =
        response;
      commit("SET_AUTH_DATA", {
        accessToken,
        refreshToken,
        accessTokenExpiration,
        userData: { email: userEmail, roles },
      });
      
      // Redirect to intended page or default authorized route
      if (redirectTo && redirectTo !== '/login') {
        router.push(decodeURIComponent(redirectTo));
      } else {
        // Pass isMobileApp flag to get appropriate route
        const isMobileApp = getMobileAppFromCookie();
        router.push(getAuthedRoute(roles[0], isMobileApp));
      }
      
      return response;
    } catch (error) {
      // show message
      store.commit("message/showMessage", {
        message: " اسم المستخدم أو كلمة المرور غير صحيحة",
        type: "error",
      });
      console.log("status", error);
      throw error;
    }
  },
  async refreshToken({ commit, state }, { redirectOnFailure = true } = {}) {
    let refreshToken = getRefreshToken();

    // Don't try to refresh if no refresh token exists
    if (!refreshToken) {
      console.error("No refresh token available");
      commit("CLEAR_AUTH_DATA");
      if (redirectOnFailure) {
        const currentPath = router.currentRoute.value.fullPath;
        if (currentPath && currentPath !== '/login') {
          router.push(`/login?redirect=${encodeURIComponent(currentPath)}`);
        } else {
          router.push("/login");
        }
      }
      return;
    }

    try {
      const response = await refreshTokenUsingApi(refreshToken);

      // Validate response has required fields
      const {
        accessToken,
        accessTokenExpiration,
        email,
        roles,
        refreshToken: newRefreshToken,
      } = response;

      if (
        !accessToken ||
        !accessTokenExpiration ||
        !email ||
        !roles ||
        !newRefreshToken
      ) {
        console.error("Invalid response from refresh token endpoint", response);
        if (redirectOnFailure) {
          store.commit("message/showMessage", {
            message: "لم نتمكن من تمديد الجلسة الحالية - استجابة غير صالحة",
            type: "error",
          });
          commit("CLEAR_AUTH_DATA");
          const currentPath = router.currentRoute.value.fullPath;
          if (currentPath && currentPath !== '/login') {
            router.push(`/login?redirect=${encodeURIComponent(currentPath)}`);
          } else {
            router.push("/login");
          }
        }
        return;
      }

      // Update auth data with new tokens
      commit("SET_AUTH_DATA", {
        accessToken,
        refreshToken: newRefreshToken,
        accessTokenExpiration,
        userData: { email, roles },
      });

      // Notify mobile app about token refresh
      notifyMobileAppTokenRefresh(accessToken, newRefreshToken, accessTokenExpiration);

      return response;
    } catch (error) {
      console.error("Error refreshing token:", error);
      if (redirectOnFailure) {
        store.commit("message/showMessage", {
          message: error.response?.data?.message || "فشل تحديث رمز الوصول",
          type: "error",
        });
        commit("CLEAR_AUTH_DATA");
        const currentPath = router.currentRoute.value.fullPath;
        if (currentPath && currentPath !== '/login') {
          router.push(`/login?redirect=${encodeURIComponent(currentPath)}`);
        } else {
          router.push("/login");
        }
      }
      throw error;
    }
  },

  async logout({ commit, state }) {
    // try {
    //   if (state.refreshToken) {
    //     await revokeToken(state.refreshToken);
    //   }
    // } catch (error) {
    //   // console.log('finally in logout action');
    //   localStorage.setItem(
    //     "reason-logout",
    //     JSON.stringify({
    //       msg: "finally in logout action",
    //       error,
    //     })
    //   );
    //   console.error("Error revoking token:", error);
    // } finally {
    //   commit("CLEAR_AUTH_DATA");
    //   router.push("/login");
    // }
    commit("CLEAR_AUTH_DATA");

    // Notify mobile app about logout
    notifyMobileAppLogout();

    router.push("/login");
  },

  async checkAuth({ dispatch, state }) {
    if (!state.accessToken) return false;

    if (isTokenExpired()) {
      try {
        await dispatch("refreshToken");
        return true;
      } catch {
        return false;
      }
    }

    return true;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
